import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  CALLSECT_META_DESCRIPTION,
  CALLSECT_SEO_TITLE,
  CALLSECT_SOCIAL_DESCRIPTION,
  CALLSECT_SOCIAL_IMAGE_ALT,
  CALLSECT_SOCIAL_IMAGE_PATH,
  CALLSECT_SOCIAL_TITLE,
} from "@/lib/positioning";

const SITE_URL = "https://callsect.com";
const SOCIAL_IMAGE = `${SITE_URL}${CALLSECT_SOCIAL_IMAGE_PATH}`;
const DEFAULT_DESCRIPTION = CALLSECT_META_DESCRIPTION;


const HOME_FAQ_SCHEMA = [
  {
    question: "How does Callsect get my conversations?",
    answer: "Connect Google Meet once and Callsect automatically imports the meeting transcript after the call. If you already have the conversation elsewhere, you can upload a transcript, audio, or video directly.",
  },
  {
    question: "Does Callsect record or join my calls?",
    answer: "No. Callsect doesn't join your meetings as a bot and doesn't record your calls. With Google Meet, it reads the transcript generated after the meeting.",
  },
  {
    question: "What happens to uploaded audio and my conversation data?",
    answer: "The original recording isn't stored in Callsect's database. Uploaded audio is processed for transcription and temporary copies are deleted after the transcription attempt. The resulting transcript and generated analysis stay in your account so Callsect can carry context forward. You can delete individual calls or your account data at any time. See exactly how data is handled.",
  },
  {
    question: "What does “how you write and speak” mean?",
    answer: "Callsect learns two different kinds of patterns. Writing DNA learns from the writing examples you provide and shapes both CRM notes and follow-up emails around your structure, detail, and tone. Speaking DNA is learned from your calls and helps Callsect understand how you communicate and prepare you for the next conversation.",
  },
  {
    question: "Do I need to change how I run my calls?",
    answer: "No. Callsect works after the conversation. It turns the calls you're already having into analysis, relationship context, notes, follow-ups, coaching, and preparation for the next one.",
  },
  {
    question: "Is Callsect free?",
    answer: "Yes. Callsect is currently free to use, with usage limits. There are no paid plans yet, but paid plans will be introduced in the future.",
  },
  {
    question: "Can my team use Callsect together?",
    answer: "Callsect is currently designed around individual customer-facing professionals and their customer relationships. Shared team workspaces aren't available yet.",
  },
  {
    question: "Does Callsect integrate with our CRM?",
    answer: "Not currently. Callsect works without a CRM integration today. Native CRM integrations are something we're exploring for teams that need customer context to stay connected to their existing workflow.",
  },
  {
    question: "Can my company use Callsect?",
    answer: "Callsect is currently self-serve for individual users. If you're interested in using it across a customer-facing team, get in touch.",
  },
];

const GONG_ALTERNATIVE_FAQ_SCHEMA = [
  {
    question: "Is Callsect a replacement for Gong?",
    answer: "Not as an organization-wide revenue platform. Callsect can be an alternative for an individual account manager, customer-success professional, or seller whose main need is to understand recurring customer calls, carry relationship context forward, document them, coach themselves, and prepare the next conversation.",
  },
  {
    question: "Does Callsect do revenue forecasting or pipeline management?",
    answer: "No. Callsect focuses on the conversation and the ongoing customer relationship. It does not provide revenue forecasting, pipeline management, or manager-level revenue dashboards.",
  },
  {
    question: "Can Callsect track a customer relationship across multiple calls?",
    answer: "Yes. Callsect carries forward stakeholders, commitments, objections, unresolved issues, relationship changes, and other context from the customer calls you analyze.",
  },
  {
    question: "How does pricing differ?",
    answer: "Callsect is currently free to use with usage limits. Gong's public pricing page says licenses are priced per user and that a platform fee is based on the number of users supported; Gong provides a customized proposal.",
  },
];

const PUBLIC_ROUTES = {
  "/": {
    title: CALLSECT_SEO_TITLE,
    socialTitle: CALLSECT_SOCIAL_TITLE,
    description: DEFAULT_DESCRIPTION,
    socialDescription: CALLSECT_SOCIAL_DESCRIPTION,
    indexable: true,
    schema: true,
  },
  "/sales-call-review": {
    title: "Sales Call Review: What Changed the Outcome | Callsect",
    socialTitle: "Sales call review that goes beyond a summary",
    description:
      "Learn how to review sales calls using evidence, coaching, relationship context, and next-call preparation—not just transcripts or talk-time scores.",
    indexable: true,
  },
  "/sales-call-analysis-software": {
    title: "Sales Call Analysis Software Beyond Summaries | Callsect",
    socialTitle: "Sales call analysis software that carries the reasoning forward",
    description:
      "Sales call analysis software should connect evidence to interpretation, coaching, next moves, and relationship context—not stop at transcripts and scorecards.",
    indexable: true,
  },
  "/conversation-intelligence-vs-call-summarization": {
    title: "Conversation Intelligence vs Call Summarization | Callsect",
    socialTitle: "Conversation intelligence is more than a call summary",
    description:
      "Conversation intelligence should interpret what changed, why it matters, what to coach, and what happens next—not just compress a call into notes.",
    indexable: true,
  },
  "/prepare-for-next-customer-call": {
    title: "How to Prepare for Your Next Customer Call | Callsect",
    socialTitle: "Prepare the next customer call from the previous conversation",
    description:
      "Learn how to prepare for the next customer call using previous-conversation evidence, relationship context, coaching, open loops, and a concrete next-call objective.",
    indexable: true,
  },
  "/crm-notes-after-sales-call": {
    title: "CRM Notes After a Sales Call: What to Capture | Callsect",
    socialTitle: "Write CRM notes that preserve what changed in the call",
    description:
      "Learn what to capture in CRM notes after a sales call: blockers, stakeholders, commitments, relationship changes, and a clear next step without rewriting the transcript.",
    indexable: true,
  },
  "/sales-call-debrief-template": {
    title: "Sales Call Debrief Template: Evidence to Next Move | Callsect",
    socialTitle: "A sales call debrief template built around evidence and action",
    description:
      "Use a practical sales call debrief template to capture evidence, what changed, a better approach, and the next move instead of writing another recap.",
    indexable: true,
  },
  "/follow-up-email-from-sales-call-transcript": {
    title: "Follow-up Email From a Sales Call Transcript | Callsect",
    socialTitle: "Turn a sales call transcript into a useful follow-up email",
    description:
      "Turn a sales call transcript into a concise follow-up email by extracting the outcome, commitments, open loops, and relationship context that matter next.",
    indexable: true,
  },
  "/self-coaching-sales-calls": {
    title: "How to Self-Coach Your Sales Calls | Callsect",
    socialTitle: "Self-coach sales calls from real customer moments",
    description:
      "Learn how to self-coach sales calls using evidence, interpretation, a better approach, example wording, and lessons you can carry into the next conversation.",
    indexable: true,
  },
  "/renewal-call-preparation": {
    title: "How to Prepare for a Renewal Call | Callsect",
    socialTitle: "Prepare renewal calls from the customer history",
    description:
      "Prepare for a renewal call using conversation history, unresolved risk, stakeholders, commitments, and a concrete renewal objective instead of starting from a blank checklist.",
    indexable: true,
  },
  "/gong-alternative-for-account-managers": {
    title: "Gong Alternative for Account Managers | Callsect",
    socialTitle: "Gong vs Callsect for account managers: different scope, different workflow",
    description:
      "Compare Gong and Callsect for account managers: broader Revenue AI and forecasting vs a focused self-serve workflow for recurring customer relationships.",
    indexable: true,
    schema: true,
  },
  "/about": {
    title: "About Callsect | Built from Real Customer Work",
    socialTitle: "Built from real conversations. Refined through real work.",
    description:
      "Callsect is an independent product built and continuously refined alongside real account-management work and everyday customer conversations.",
    indexable: true,
  },
  "/product/relationship-memory": {
    title: "Customer Relationship Memory Across Calls | Callsect",
    socialTitle: "Keep every customer relationship moving.",
    description:
      "Callsect relationship memory carries stakeholders, commitments, objections, risks, and what changed across customer calls so the next conversation does not start from zero.",
    indexable: true,
  },
  "/product/crm-notes": {
    title: "CRM Notes & Follow-up Emails in Your Writing Style | Callsect",
    socialTitle: "Notes and follow-ups that still sound like you",
    description:
      "Turn customer calls into CRM-ready notes and ready-to-send follow-ups using conversation evidence, relationship context, and the writing patterns you already use.",
    indexable: true,
  },
  "/product/coaching": {
    title: "Evidence-Grounded Call Coaching | Callsect",
    socialTitle: "See what you could have said differently—and why",
    description:
      "Callsect grounds coaching in the actual customer moment, shows what you could have handled differently, and gives practical alternative wording for the next conversation.",
    indexable: true,
  },
  "/product/prepare-next-call": {
    title: "Prepare the Next Customer Call with Relationship Context | Callsect",
    socialTitle: "Walk into the next call knowing what matters",
    description:
      "Turn the previous customer conversation into a practical next-call plan with objectives, likely objections, questions, relationship context, and call-flow guidance.",
    indexable: true,
  },
  "/demo": {
    title: "Live Product Demo | Callsect",
    socialTitle: "Choose a Callsect demo for your customer calls",
    description:
      "Explore Callsect through a new-business or renewals and retention workflow, using the real product interface with synthetic customer-call data.",
    indexable: true,
  },
  "/demo/sales": {
    title: "Sales Call Demo | Callsect",
    socialTitle: "See Callsect follow a deal across conversations",
    description:
      "Follow a new-business opportunity from discovery through decision validation, coaching, CRM notes, relationship memory, and next-call preparation.",
    indexable: true,
  },
  "/demo/renewals": {
    title: "Renewals & Retention Demo | Callsect",
    socialTitle: "See Callsect carry a customer relationship forward",
    description:
      "Follow a difficult renewal through call analysis, coaching, CRM documentation, relationship memory, and next-call preparation.",
    indexable: true,
  },
  "/demo/sales/relationship": {
    title: "Sales Relationship Memory Demo | Callsect",
    socialTitle: "Deal context that compounds across calls",
    description:
      "Explore how Callsect preserves stakeholders, objections, commitments, and decision context across multiple sales conversations.",
    indexable: true,
  },
  "/demo/renewals/relationship": {
    title: "Relationship Memory Demo | Callsect",
    socialTitle: "Relationship memory that compounds across calls",
    description:
      "Explore how Callsect preserves the customer story, tracks what changed across calls, and prepares the next move.",
    indexable: true,
  },
  "/privacy": {
    title: "Privacy Notice | Callsect",
    socialTitle: "Callsect Privacy Notice",
    description:
      "How Callsect processes account data, temporary audio, transcripts, generated outputs, product events, and private-beta requests.",
    indexable: true,
  },
  "/terms": {
    title: "Private Beta Terms | Callsect",
    socialTitle: "Callsect Private Beta Terms",
    description: "The terms governing access to and use of the Callsect private beta.",
    indexable: true,
  },
  "/data-handling": {
    title: "Data Handling | Callsect",
    socialTitle: "How Callsect handles call data",
    description:
      "A plain-language overview of how Callsect processes audio, transcripts, generated outputs, retention, and deletion.",
    indexable: true,
  },
};

const PRIVATE_ROUTE_TITLES = [
  ["/dashboard", "Dashboard | Callsect"],
  ["/upload", "New Call | Callsect"],
  ["/import-transcript", "Import Transcript | Callsect"],
  ["/calls/", "Conversation | Callsect"],
  ["/customers/", "Customer Relationship | Callsect"],
  ["/customers", "Customers | Callsect"],
  ["/account", "Account | Callsect"],
  ["/profile", "Profile | Callsect"],
  ["/admin", "Admin | Callsect"],
  ["/prepare-next-call", "Prepare Next Call | Callsect"],
  ["/legal-consent", "Legal Consent | Callsect"],
  ["/login", "Sign In | Callsect"],
  ["/forgot-password", "Reset Password | Callsect"],
  ["/reset-password", "Choose a New Password | Callsect"],
  ["/verify-email", "Verify Email | Callsect"],
];

function upsertMeta(selector, attributes) {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement("meta");
    document.head.appendChild(element);
  }
  Object.entries(attributes).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });
}

function upsertLink(selector, attributes) {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement("link");
    document.head.appendChild(element);
  }
  Object.entries(attributes).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });
}

function resolveRoute(pathname) {
  const publicMeta = PUBLIC_ROUTES[pathname];
  if (publicMeta) return publicMeta;

  const privateMatch = PRIVATE_ROUTE_TITLES.find(([prefix]) => (
    prefix.endsWith("/") ? pathname.startsWith(prefix) : pathname === prefix
  ));

  return {
    title: privateMatch?.[1] || "Callsect",
    socialTitle: "Callsect",
    description: "Private Callsect workspace.",
    indexable: false,
  };
}

function canonicalFor(pathname, indexable) {
  if (!indexable) return `${SITE_URL}/`;
  return pathname === "/" ? `${SITE_URL}/` : `${SITE_URL}${pathname}`;
}

function updateStructuredData(enabled, pathname) {
  const existing = document.getElementById("callsect-site-schema");
  if (!enabled) {
    existing?.remove();
    return;
  }

  const script = existing || document.createElement("script");
  script.id = "callsect-site-schema";
  script.type = "application/ld+json";
  const graph = [
      {
        "@type": "Organization",
        "@id": `${SITE_URL}/#organization`,
        name: "Callsect",
        url: `${SITE_URL}/`,
      },
      {
        "@type": "WebSite",
        "@id": `${SITE_URL}/#website`,
        name: "Callsect",
        url: `${SITE_URL}/`,
        description: DEFAULT_DESCRIPTION,
        publisher: { "@id": `${SITE_URL}/#organization` },
        inLanguage: "en",
      },
      {
        "@type": "SoftwareApplication",
        "@id": `${SITE_URL}/#software`,
        name: "Callsect",
        applicationCategory: "BusinessApplication",
        applicationSubCategory: "Relationship intelligence for customer calls",
        operatingSystem: "Web",
        url: `${SITE_URL}/`,
        image: SOCIAL_IMAGE,
        description: DEFAULT_DESCRIPTION,
        brand: { "@id": `${SITE_URL}/#organization` },
        audience: {
          "@type": "Audience",
          audienceType: "Professionals whose success depends on customer calls",
        },
        featureList: [
          "Relationship intelligence across customer calls",
          "AI call analysis and longitudinal relationship memory",
          "CRM-ready notes in the user's writing voice",
          "Evidence-grounded coaching",
          "Customer-calibrated next-call preparation",
          "Multilingual customer-call workflows",
        ],
      },
    ];

  const faqSchema = pathname === "/"
    ? HOME_FAQ_SCHEMA
    : pathname === "/gong-alternative-for-account-managers"
      ? GONG_ALTERNATIVE_FAQ_SCHEMA
      : null;

  if (faqSchema) {
    graph.push({
      "@type": "FAQPage",
      "@id": pathname === "/" ? `${SITE_URL}/#faq` : `${SITE_URL}${pathname}#faq`,
      isPartOf: { "@id": `${SITE_URL}/#website` },
      mainEntity: faqSchema.map(({ question, answer }) => ({
        "@type": "Question",
        name: question,
        acceptedAnswer: {
          "@type": "Answer",
          text: answer,
        },
      })),
    });
  }

  script.textContent = JSON.stringify({
    "@context": "https://schema.org",
    "@graph": graph,
  });

  if (!existing) document.head.appendChild(script);
}

export default function SeoManager() {
  const { pathname, search } = useLocation();

  useEffect(() => {
    if (typeof document === "undefined") return;

    const route = resolveRoute(pathname);
    const registrationMode = pathname === "/signup"
      || (pathname === "/login" && new URLSearchParams(search).get("mode") === "register");
    const pageTitle = registrationMode ? "Create Account | Callsect" : route.title;
    const canonicalUrl = canonicalFor(pathname, route.indexable);
    const robots = route.indexable
      ? "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1"
      : "noindex,nofollow,noarchive";

    document.title = pageTitle;

    upsertMeta('meta[name="description"]', {
      name: "description",
      content: route.description,
    });
    upsertMeta('meta[name="robots"]', { name: "robots", content: robots });
    upsertMeta('meta[name="googlebot"]', { name: "googlebot", content: robots });

    upsertMeta('meta[property="og:type"]', { property: "og:type", content: "website" });
    upsertMeta('meta[property="og:site_name"]', { property: "og:site_name", content: "Callsect" });
    upsertMeta('meta[property="og:locale"]', { property: "og:locale", content: "en_US" });
    upsertMeta('meta[property="og:title"]', {
      property: "og:title",
      content: route.socialTitle || pageTitle,
    });
    upsertMeta('meta[property="og:description"]', {
      property: "og:description",
      content: route.socialDescription || route.description,
    });
    upsertMeta('meta[property="og:url"]', { property: "og:url", content: canonicalUrl });
    upsertMeta('meta[property="og:image"]', { property: "og:image", content: SOCIAL_IMAGE });
    upsertMeta('meta[property="og:image:secure_url"]', {
      property: "og:image:secure_url",
      content: SOCIAL_IMAGE,
    });
    upsertMeta('meta[property="og:image:type"]', {
      property: "og:image:type",
      content: "image/png",
    });
    upsertMeta('meta[property="og:image:width"]', { property: "og:image:width", content: "1200" });
    upsertMeta('meta[property="og:image:height"]', { property: "og:image:height", content: "630" });
    upsertMeta('meta[property="og:image:alt"]', {
      property: "og:image:alt",
      content: CALLSECT_SOCIAL_IMAGE_ALT,
    });

    upsertMeta('meta[name="twitter:card"]', {
      name: "twitter:card",
      content: "summary_large_image",
    });
    upsertMeta('meta[name="twitter:title"]', {
      name: "twitter:title",
      content: route.socialTitle || pageTitle,
    });
    upsertMeta('meta[name="twitter:description"]', {
      name: "twitter:description",
      content: route.socialDescription || route.description,
    });
    upsertMeta('meta[name="twitter:image"]', { name: "twitter:image", content: SOCIAL_IMAGE });
    upsertMeta('meta[name="twitter:image:alt"]', {
      name: "twitter:image:alt",
      content: CALLSECT_SOCIAL_IMAGE_ALT,
    });

    upsertLink('link[rel="canonical"]', { rel: "canonical", href: canonicalUrl });

    const llmsDescriptionLink = document.head.querySelector('link[rel="describedby"]');
    if (route.indexable) {
      upsertLink('link[rel="describedby"]', {
        rel: "describedby",
        href: `${SITE_URL}/llms.txt`,
      });
    } else {
      llmsDescriptionLink?.remove();
    }

    updateStructuredData(Boolean(route.schema), pathname);
  }, [pathname, search]);

  return null;
}
